import React, { useState, useEffect } from "react";
import axios from "axios";
import VendorMasterForm from "./VendorMasterForm";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";
import "./VendorQuote.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const emptyExpense = {
  date: "",
  vendorId: "",
  vendorName: "",
  vendorCode: "",
  workType: "",
  paymentAmt: "",
  mode: "",
  gstInvoice: "",
  employeeNote: "",
};

const getAuthConfig = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("userToken") ||
    localStorage.getItem("adminToken") ||
    storedUser?.token ||
    storedUser?.accessToken;

  console.log("AUTH TOKEN FOUND:", token ? "YES" : "NO");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const formatRole = (role) =>
  String(role || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");

const formatINR = (amount) => `${Number(amount || 0).toFixed(2)}`;
const isZeroTargetItem = (item = {}) => {
  // Explicit zero is valid only when the record marks it as configured.
  // Never treat an absent target as complimentary just because Number(undefined) is zero.
  const hasZero = ["originalTargetCost", "targetCost", "vendorTargetCost"].some(
    (key) =>
      item[key] !== undefined &&
      item[key] !== null &&
      item[key] !== "" &&
      Number(item[key]) === 0,
  );
  return (
    hasZero &&
    (item.isComplimentary === true ||
      item.targetMissing === false ||
      /\bcomplimentary\b/i.test(String(item.itemName || "")))
  );
};
const isMissingTargetItem = (item = {}) =>
  !isZeroTargetItem(item) &&
  (item.targetMissing === true ||
    positiveMoney(
      item.originalTargetCost,
      item.targetCost,
      item.vendorTargetCost,
    ) <= 0);

const positiveMoney = (...values) => {
  for (const value of values) {
    const amount = Number(value);
    if (Number.isFinite(amount) && amount > 0) return amount;
  }
  return 0;
};

const formatDifference = (value) => {
  const amount = Number(value || 0);

  const formatted = Math.abs(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (amount > 0) return `+${formatted}`;
  if (amount < 0) return `−${formatted}`;

  return `${formatted}`;
};
const formatDifferenceWithPercent = (difference, target) => {
  const base = Number(target);
  const amount = formatDifference(difference);
  if (!Number.isFinite(base) || base <= 0) return `${amount} (—)`;
  const percent = (Number(difference) / base) * 100;
  const sign = percent > 0 ? "+" : percent < 0 ? "−" : "";
  return `${amount} (${sign}${Math.abs(percent).toFixed(2)}%)`;
};

const formatDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Convert any stored payment-proof value into a browser-safe URL.
// Supports:
// 1) "/uploads/payments/file.png"
// 2) "/api/files/payments/file.png"
// 3) Windows absolute paths such as:
//    "C:\\SvbSystem\\Backend\\uploads\\payments\\file.png"
const getPaymentProofUrl = (paymentProof) => {
  if (!paymentProof) return "";

  const raw = String(paymentProof).trim();

  // Already a complete URL.
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  // Normalize Windows backslashes to forward slashes.
  const normalized = raw.replace(/\\/g, "/");

  // If DB contains an absolute Windows/server path, keep only the part
  // after "/uploads/" and serve it through the backend static route.
  const uploadsIndex = normalized.toLowerCase().indexOf("/uploads/");
  if (uploadsIndex !== -1) {
    const relativePath = normalized.substring(
      uploadsIndex + "/uploads/".length,
    );

    return `${BASE_URL}/api/files/${relativePath}`;
  }

  // Already stored using the API static route.
  if (normalized.startsWith("/api/files/")) {
    return `${BASE_URL}${normalized}`;
  }

  // Already stored using /uploads route.
  if (normalized.startsWith("/uploads/")) {
    return `${BASE_URL}${normalized}`;
  }

  // Fallback for values such as "payments/file.png".
  return `${BASE_URL}/api/files/${normalized.replace(/^\/+/, "")}`;
};
const Block = ({ title, variant = "default", children }) => (
  <section className={`vq-block vq-block-${variant}`}>
    <div className="vq-block-title">{title}</div>
    <div className="vq-block-body">{children}</div>
  </section>
);

const Input = ({ value, onChange, className = "", ...props }) => (
  <input
    {...props}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className={`vq-input ${className}`}
  />
);

const Select = ({ value, onChange, options, placeholder, className = "" }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className={`vq-input vq-select ${className}`}
  >
    <option value="">{placeholder}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

// Reusable compact sorting for every table.
const sortRows = (rows, sort, getters) => {
  if (!sort.key || !getters[sort.key]) return rows;
  return [...rows].sort((a, b) => {
    const x = getters[sort.key](a);
    const y = getters[sort.key](b);
    const numeric = typeof x === "number" && typeof y === "number";
    const result = numeric
      ? x - y
      : String(x ?? "").localeCompare(String(y ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
    return sort.direction === "asc" ? result : -result;
  });
};
const useTableSort = () => {
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const toggle = (key) =>
    setSort((old) => ({
      key,
      direction: old.key === key && old.direction === "asc" ? "desc" : "asc",
    }));
  return { sort, toggle };
};
const SortTh = ({ label, sortKey, sort, toggle, children, ...props }) => (
  <th
    {...props}
    aria-sort={
      sort.key === sortKey
        ? sort.direction === "asc"
          ? "ascending"
          : "descending"
        : "none"
    }
  >
    <button
      type="button"
      className="vq-sort-btn"
      onClick={() => toggle(sortKey)}
      title={`Sort ${label || sortKey}`}
    >
      {children || label}
      <span className="vq-sort-icon">
        {sort.key === sortKey ? (sort.direction === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </button>
  </th>
);

const RequestTable = ({
  rows,
  showApprove,
  showReject,
  showMarkPaid,
  showAdminActions = false,
  onEdit,
  onDelete,
  onApprove,
  onReject,
  onMarkPaid,
  resolveVendorType,
  currentUserId = "",
  isAdmin = false,
  onSaveNote,
  noteSavingId,
}) => {
  const requestSort = useTableSort();
  const [selectedPaymentDates, setSelectedPaymentDates] = useState({});
  const sortedRequestRows = sortRows(rows, requestSort.sort, {
    date: (r) => new Date(r.date || 0).getTime(),
    paymentDate: (r) =>
      r.paymentDate ? new Date(`${r.paymentDate}T00:00:00`).getTime() : 0,
    vendorName: (r) => r.vendorName,
    vendorType: (r) => resolveVendorType(r),
    vendorCode: (r) => r.vendorCode,
    workType: (r) => r.workType,
    paymentAmt: (r) => Number(r.paymentAmt || 0),
    mode: (r) => r.mode,
    gstInvoice: (r) => r.gstInvoice,
    status: (r) => r.status,
    paymentProof: (r) => (r.paymentProof ? "Yes" : "No"),
    employeeNote: (r) => r.employeeNote,
    approvalRemark: (r) => r.approvalRemark || r.rejectRemark,
  });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [draftNote, setDraftNote] = useState("");

  // An authenticated user can edit their own request note. Admins can
  // edit the note, but cannot change it once the request is paid.
  const canEditNote = (request) => {
    const ownerId = String(request.createdBy?._id || request.createdBy || "");
    const ownerMatch =
      Boolean(currentUserId) && ownerId === String(currentUserId);
    return (
      ["PENDING_APPROVAL", "APPROVED"].includes(request.status) &&
      (isAdmin || ownerMatch)
    );
  };

  const submitNote = async (request) => {
    if (!canEditNote(request) || noteSavingId) return;
    const saved = await onSaveNote(request._id, draftNote);
    if (saved) {
      setEditingNoteId(null);
      setDraftNote("");
    }
  };

  if (!rows.length) {
    return <div className="vq-empty-state">No records found.</div>;
  }

  return (
    <div className="vq-table-scroll">
      <table className="vq-table vq-request-table">
        <thead>
          <tr>
            <SortTh
              label="Date"
              sortKey="date"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Actual Payment Date"
              sortKey="paymentDate"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Vendor Name"
              sortKey="vendorName"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Vendor Type"
              sortKey="vendorType"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Vendor Code"
              sortKey="vendorCode"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Work Type"
              sortKey="workType"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              className="vq-num"
              label="Payment Amount"
              sortKey="paymentAmt"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Payment Mode"
              sortKey="mode"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="GST Invoice"
              sortKey="gstInvoice"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Status"
              sortKey="status"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Payment Proof"
              sortKey="paymentProof"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              style={{ minWidth: 230 }}
              label="User Note"
              sortKey="employeeNote"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            <SortTh
              label="Admin Remark"
              sortKey="approvalRemark"
              sort={requestSort.sort}
              toggle={requestSort.toggle}
            />
            {(showApprove ||
              showReject ||
              showMarkPaid ||
              showAdminActions) && <th>Action</th>}
          </tr>
        </thead>

        <tbody>
          {sortedRequestRows.map((r) => (
            <tr key={r._id}>
              <td>{formatDate(r.date)}</td>
              <td>
                {showMarkPaid && r.status === "APPROVED" ? (
                  <input
                    type="date"
                    className="vq-input"
                    aria-label={`Actual payment date for ${r.vendorName || "vendor"}`}
                    value={selectedPaymentDates[r._id] || ""}
                    onChange={(e) =>
                      setSelectedPaymentDates((prev) => ({
                        ...prev,
                        [r._id]: e.target.value,
                      }))
                    }
                    style={{ minWidth: 140, padding: "5px 6px" }}
                  />
                ) : r.status === "PAID" && r.paymentDate ? (
                  formatDate(r.paymentDate)
                ) : (
                  "-"
                )}
              </td>
              <td className="vq-strong">{r.vendorName || "-"}</td>
              <td>{resolveVendorType(r)}</td>
              <td>{r.vendorCode || "-"}</td>
              <td>{r.workType || "-"}</td>
              <td className="vq-num vq-strong">
                <div>{formatINR(r.paymentAmt)}</div>
                {["APPROVED", "PAID"].includes(r.status) && (
                  <div
                    style={{
                      marginTop: "3px",
                      fontSize: "10px",
                      color: "#15803d",
                      fontWeight: 900,
                    }}
                  >
                    ADMIN APPROVED
                  </div>
                )}
              </td>
              <td>
                <b>{r.mode || "-"}</b>
                {["APPROVED", "PAID"].includes(r.status) && (
                  <div
                    style={{
                      marginTop: "3px",
                      fontSize: "10px",
                      color: "#15803d",
                      fontWeight: 900,
                    }}
                  >
                    FINAL MODE
                  </div>
                )}
              </td>
              <td>{r.gstInvoice || "-"}</td>
              <td>
                <span
                  className={`vq-status vq-status-${String(r.status || "").toLowerCase()}`}
                >
                  {r.status || "-"}
                </span>
              </td>
              <td>
                {r.paymentProof ? (
                  <a
                    href={getPaymentProofUrl(r.paymentProof)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={getPaymentProofUrl(r.paymentProof)}
                    className="vq-btn vq-btn-outline vq-btn-small"
                  >
                    View Proof
                  </a>
                ) : (
                  <span className="vq-muted">No Proof</span>
                )}
              </td>
              <td
                style={{ minWidth: 230, maxWidth: 350, verticalAlign: "top" }}
              >
                {editingNoteId === r._id ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    <textarea
                      className="vq-input"
                      aria-label={`Edit note for ${r.vendorName || "vendor"}`}
                      rows={4}
                      maxLength={3000}
                      value={draftNote}
                      disabled={noteSavingId === r._id}
                      onChange={(e) => setDraftNote(e.target.value)}
                      style={{
                        width: "100%",
                        minHeight: 84,
                        boxSizing: "border-box",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        resize: "vertical",
                      }}
                    />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      <button
                        type="button"
                        className="vq-btn vq-btn-primary vq-btn-small"
                        disabled={noteSavingId === r._id}
                        onClick={() => submitNote(r)}
                      >
                        {noteSavingId === r._id ? "Saving..." : "Save Note"}
                      </button>
                      <button
                        type="button"
                        className="vq-btn vq-btn-outline vq-btn-small"
                        disabled={noteSavingId === r._id}
                        onClick={() => {
                          setEditingNoteId(null);
                          setDraftNote("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 5 }}>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        lineHeight: 1.5,
                      }}
                    >
                      {r.employeeNote || "-"}
                    </div>
                    {canEditNote(r) && (
                      <button
                        type="button"
                        className="vq-btn vq-btn-outline vq-btn-small"
                        style={{ justifySelf: "start" }}
                        onClick={() => {
                          setEditingNoteId(r._id);
                          setDraftNote(r.employeeNote || "");
                        }}
                      >
                        {r.employeeNote ? "Edit Note" : "+ Add Note"}
                      </button>
                    )}
                  </div>
                )}
              </td>
              <td
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  minWidth: 150,
                  maxWidth: 300,
                }}
              >
                {r.approvalRemark || r.rejectRemark || "-"}
              </td>

              {(showApprove ||
                showReject ||
                showMarkPaid ||
                showAdminActions) && (
                <td>
                  <div className="vq-actions-inline">
                    {showApprove && r.status === "PENDING_APPROVAL" && (
                      <button
                        className="vq-btn vq-btn-success vq-btn-small"
                        onClick={() => onApprove(r._id)}
                      >
                        Approve
                      </button>
                    )}

                    {showReject && r.status === "PENDING_APPROVAL" && (
                      <button
                        className="vq-btn vq-btn-danger vq-btn-small"
                        onClick={() => onReject(r._id)}
                      >
                        Reject
                      </button>
                    )}

                    {showMarkPaid && r.status === "APPROVED" && (
                      <button
                        type="button"
                        className="vq-btn vq-btn-primary vq-btn-small"
                        onClick={() => {
                          const date = selectedPaymentDates[r._id];
                          if (!date) {
                            alert("Please select Actual Payment Date first.");
                            return;
                          }
                          onMarkPaid(r._id, date);
                        }}
                      >
                        Mark Paid
                      </button>
                    )}
                    {showAdminActions && r.status === "APPROVED" && (
                      <>
                        <button
                          type="button"
                          className="vq-btn vq-btn-outline vq-btn-small"
                          onClick={() => onEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="vq-btn vq-btn-danger vq-btn-small"
                          onClick={() => onDelete(r)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const VendorQuote = ({ project, user: propUser }) => {
  const navigate = useNavigate();
  const poSort = useTableSort();
  const itemSort = useTableSort();
  const duplicateSort = useTableSort();
  const ledgerSort = useTableSort();
  const [paymentModes, setPaymentModes] = useState([]);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [user, setUser] = useState(propUser || null);
  const [vendors, setVendors] = useState([]);
  const [poRows, setPoRows] = useState([]);
  const [loadingPO, setLoadingPO] = useState(false);
  const [expense, setExpense] = useState(emptyExpense);
  const [allPaymentRows, setAllPaymentRows] = useState([]);
  const [pendingApprovalRows, setPendingApprovalRows] = useState([]);
  const [approvedRows, setApprovedRows] = useState([]);
  const [ledgerVendorId, setLedgerVendorId] = useState("");
  const [editingApproved, setEditingApproved] = useState(null);
  const [approvedEditForm, setApprovedEditForm] = useState(null);
  const [approvedActionBusy, setApprovedActionBusy] = useState(false);
  const [noteSavingId, setNoteSavingId] = useState(null);
  const currentUserId = String(user?._id || user?.id || user?.userId || "");
  const role = formatRole(user?.role || localStorage.getItem("userRole"));
  const isAdmin = role === "admin";
  const isAccountant = role === "accountant";
  const isHeadAccountant = role === "head_accountant";
  const isProjectManager = role === "project_manager";
  const canViewVendorQuote = canAccess("vendorQuote", "view");
  const canShowPOSummary = canAccess("vendorQuote", "vendorQuoteShowPOSummary");
  const canShowDuplicateAlert = canAccess(
    "vendorQuote",
    "vendorQuoteShowDuplicateAlert",
  );
  const canExportExcel = canAccess("vendorQuote", "vendorQuoteExportExcel");
  const canCreatePayment = canAccess("vendorQuote", "vendorQuoteCreatePayment");
  const canShowPaymentInfo = canAccess(
    "vendorQuote",
    "vendorQuoteShowPaymentInfo",
  );
  const canShowPendingApproval = canAccess(
    "vendorQuote",
    "vendorQuoteShowPendingApproval",
  );
  const canApproveReject = canAccess("vendorQuote", "vendorQuoteApproveReject");
  const canShowApprovedRequests = canAccess(
    "vendorQuote",
    "vendorQuoteShowApprovedRequests",
  );
  const canMarkPaid = canAccess("vendorQuote", "vendorQuoteMarkPaid");
  const canShowVendorMaster = canAccess(
    "vendorQuote",
    "vendorQuoteShowVendorMaster",
  );
  const canViewVendorPO = canAccess("vendorPO", "view");
  const canSeeVendorButton = canViewVendorPO || canShowVendorMaster;
  const [paymentProof, setPaymentProof] = useState(null);
  const [markPaidModal, setMarkPaidModal] = useState(null);
  const [actualPaymentDate, setActualPaymentDate] = useState("");
  const [actualPaymentProof, setActualPaymentProof] = useState(null);
  const [markPaidSaving, setMarkPaidSaving] = useState(false);
  useEffect(() => {
    if (!propUser) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error("Failed to parse user:", e);
        }
      }
    }
  }, [propUser]);
  useEffect(() => {
    const fetchPaymentModes = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/vendor-payment-requests/payment-modes`,
          getAuthConfig(),
        );

        setPaymentModes(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error(
          "Payment modes fetch error:",
          error?.response?.data || error,
        );
        setPaymentModes([]);
      }
    };

    fetchPaymentModes();
  }, []);
  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/vendors`, getAuthConfig());
      setVendors(res.data || []);
    } catch (err) {
      console.error("Error fetching vendors:", err?.response?.data || err);
    }
  };

  // Resolve vendor type from Vendor Master. Existing PO/payment rows may not
  // contain vendorType, so match by ID first, then vendor code; use vendor name
  // only when it uniquely matches a master record.
  const resolveVendorType = (record = {}) => {
    const id = String(record.vendorId || record._id || "").trim();
    const code = String(record.vendorCode || "")
      .trim()
      .toLowerCase();
    const name = String(record.vendorName || record.fullName || "")
      .trim()
      .toLowerCase();

    const byId = id
      ? vendors.find((v) => String(v._id || v.id || "") === id)
      : null;
    const byCode =
      !byId && code
        ? vendors.find(
            (v) =>
              String(v.vendorCode || "")
                .trim()
                .toLowerCase() === code,
          )
        : null;
    const matchingNames =
      !byId && !byCode && name
        ? vendors.filter(
            (v) =>
              String(v.fullName || v.vendorName || "")
                .trim()
                .toLowerCase() === name,
          )
        : [];
    const master =
      byId || byCode || (matchingNames.length === 1 ? matchingNames[0] : null);

    return master?.vendorType || record.vendorType || "-";
  };

  const fetchVendorPOSummary = async () => {
    try {
      if (!project?.name) return;

      setLoadingPO(true);

      const res = await axios.get(
        `${BASE_URL}/api/vendor-po?projectName=${encodeURIComponent(
          project.name,
        )}`,
        getAuthConfig(),
      );

      const poList = Array.isArray(res.data)
        ? res.data
        : res.data
          ? [res.data]
          : [];
      console.table(
        poList.flatMap((po) =>
          (po.items || []).map((item) => ({
            vendor: po.vendorName,
            item: item.itemName,
            targetCost: item.targetCost,
            vendorTargetCost: item.vendorTargetCost,
            originalTargetCost: item.originalTargetCost,
            targetVendorCount: item.targetVendorCount,
            vendorAmount: item.vendorAmount,
          })),
        ),
      );
      const rows = poList
        .filter((po) => Array.isArray(po.items) && po.items.length > 0)
        .map((po) => {
          const items = po.items;

          const targetCostTotal = items.reduce(
            (sum, item) => sum + Number(item.targetCost || 0),
            0,
          );
          const hasMissingTarget = items.some((item) =>
            isMissingTargetItem(item),
          );

          const vendorCostTotalFromItems = items.reduce(
            (sum, item) => sum + Number(item.vendorAmount || 0),
            0,
          );

          // Server stores net payable (base + enabled GST - enabled TDS).
          // Nullish coalescing retains a real zero total after 100% withholding.
          const vendorTotalCost = Number(
            po.totalAmount ?? vendorCostTotalFromItems,
          );

          return {
            poId: po._id,
            vendorId: po.vendorId,
            vendorName: po.vendorName || "-",
            vendorType: po.vendorType || "",
            vendorCode: po.vendorCode || "-",
            totalTargetCost: targetCostTotal,
            hasMissingTarget,
            totalVendorCost: vendorTotalCost,
            baseAmount: Number(po.baseAmount ?? vendorCostTotalFromItems),
            gstEnabled: po.gstEnabled === true,
            gstRate: Number(po.gstRate || 0),
            gstAmount: Number(po.gstAmount || 0),
            tdsEnabled: po.tdsEnabled === true,
            tdsRate: Number(po.tdsRate || 0),
            tdsAmount: Number(po.tdsAmount || 0),
            difference: targetCostTotal - vendorTotalCost,
            items,
          };
        });

      setPoRows(rows);
    } catch (err) {
      console.error("Vendor PO summary fetch error:", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
        url: err?.config?.url,
      });

      setPoRows([]);
    } finally {
      setLoadingPO(false);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      if (!project?.name) return;

      const res = await axios.get(
        `${BASE_URL}/api/vendor-payment-requests?projectName=${encodeURIComponent(
          project.name,
        )}`,
        getAuthConfig(),
      );

      const rows = Array.isArray(res.data) ? res.data : [];

      setAllPaymentRows(rows);
      setPendingApprovalRows(
        rows.filter((r) => r.status === "PENDING_APPROVAL"),
      );
      setApprovedRows(
        rows.filter((r) => ["APPROVED", "PAID"].includes(r.status)),
      );
    } catch (err) {
      console.error("Error fetching payment requests:", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
        url: err?.config?.url,
      });
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [showVendorForm]);

  useEffect(() => {
    if (project?.name) {
      fetchVendorPOSummary();
      fetchPaymentRequests();
    }
  }, [project?.name]);

  // Refresh the summary if a second vendor receives/removes an item in
  // another tab. Do not alter any unsaved payment form inputs.
  useEffect(() => {
    if (!project?.name) return undefined;
    const refresh = () => {
      if (!document.hidden) void fetchVendorPOSummary();
    };
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(refresh, 15000);
    return () => {
      window.removeEventListener("focus", refresh);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.name]);

  /* ============================================================
     ITEM SHARING ACROSS VENDORS (SAME PROJECT)
     -----------------------------------------------------------
     If the same item shows up in more than one vendor's PO, the
     item's actual/target cost is the same real-world amount, not
     one full amount per vendor. Here we work out, per item name,
     how many vendors carry it and what its real (undivided)
     target cost is - then divide that cost by the vendor count so
     both the Summary table and the Item Comparison table show the
     matching, divided figure instead of the same full cost twice.
  ============================================================ */

  const getItemKey = (item) =>
    String(item?.itemName || "")
      .trim()
      .toLowerCase();

  const getItemQty = (item) =>
    Number(item?.vendorQty || item?.qty || item?.quantity || 0);

  const itemSharedMap = {};

  poRows.forEach((vendorRow) => {
    (vendorRow.items || []).forEach((item) => {
      const key = getItemKey(item);
      if (!key) return;

      if (!itemSharedMap[key]) {
        itemSharedMap[key] = {
          vendorIds: new Set(),
          vendorNames: [],
          actualTargetCost: 0,
        };
      }

      const entry = itemSharedMap[key];

      entry.vendorIds.add(String(vendorRow.vendorId));

      if (!entry.vendorNames.includes(vendorRow.vendorName)) {
        entry.vendorNames.push(vendorRow.vendorName);
      }

      // Backend has already divided the row target. Use ORIGINAL budget to
      // avoid accidentally dividing ₹2,500 by two again (₹1,250).
      const original = Number(
        positiveMoney(
          item.originalTargetCost,
          item.targetCost,
          item.vendorTargetCost,
        ),
      );
      if (original > entry.actualTargetCost) entry.actualTargetCost = original;
    });
  });

  const getSplitInfo = (item) => {
    const key = getItemKey(item);
    const shared = itemSharedMap[key];
    const sharedCount = shared ? shared.vendorIds.size || 1 : 1;

    const actualTargetCost = shared
      ? shared.actualTargetCost
      : positiveMoney(
          item.originalTargetCost,
          item.targetCost,
          item.vendorTargetCost,
        );

    const serverHasAllocated =
      positiveMoney(item.originalTargetCost) > 0 ||
      Number(item.targetVendorCount || 0) > 0;
    return {
      sharedCount,
      actualTargetCost,
      allocatedTargetCost: serverHasAllocated
        ? positiveMoney(item.targetCost, item.vendorTargetCost)
        : actualTargetCost / sharedCount,
      sharedVendorNames: shared ? shared.vendorNames : [],
    };
  };

  /* ---- Vendor PO Summary rows, using the divided target cost ---- */
  const adjustedPoRows = poRows.map((vendorRow) => {
    const items = Array.isArray(vendorRow.items) ? vendorRow.items : [];

    let allocatedTotal = 0;
    let hasSharedItem = false;

    items.forEach((item) => {
      const { sharedCount, allocatedTargetCost } = getSplitInfo(item);

      if (sharedCount > 1) hasSharedItem = true;

      allocatedTotal += allocatedTargetCost;
    });

    return {
      ...vendorRow,
      totalTargetCost: allocatedTotal,
      difference: allocatedTotal - Number(vendorRow.totalVendorCost || 0),
      hasSharedItem,
    };
  });

  const sortedPoRows = sortRows(adjustedPoRows, poSort.sort, {
    vendorName: (r) => r.vendorName,
    vendorType: (r) => resolveVendorType(r),
    vendorCode: (r) => r.vendorCode,
    totalTargetCost: (r) => Number(r.totalTargetCost || 0),
    totalVendorCost: (r) => Number(r.totalVendorCost || 0),
    difference: (r) => Number(r.difference || 0),
  });
  const totalTargetCost = adjustedPoRows.reduce(
    (sum, row) => sum + Number(row.totalTargetCost || 0),
    0,
  );

  const totalVendorAmount = adjustedPoRows.reduce(
    (sum, row) => sum + Number(row.totalVendorCost || 0),
    0,
  );

  const totalDifference = totalTargetCost - totalVendorAmount;
  const hasAnyMissingTarget = adjustedPoRows.some(
    (row) => row.hasMissingTarget,
  );

  /* ---- Quote Item vs Vendor Price rows -----------------------------
     If the same item is quoted by more than one vendor, show it as a
     SINGLE merged row: the item's actual target cost appears once
     (not divided, not repeated), and the vendors who quoted it are
     combined into that one row along with their individual amounts.
  --------------------------------------------------------------- */
  const itemGroups = {};

  poRows.forEach((vendor) => {
    (vendor.items || []).forEach((item) => {
      const key = getItemKey(item);
      if (!key) return;

      if (!itemGroups[key]) {
        itemGroups[key] = {
          itemName: item.itemName,
          actualTargetCost: 0,
          hasMissingTarget: false,
          vendorEntries: [],
        };
      }

      const group = itemGroups[key];
      if (isMissingTargetItem(item)) group.hasMissingTarget = true;

      // One original target per project/item, regardless of vendor count.
      const original = Number(
        positiveMoney(
          item.originalTargetCost,
          item.targetCost,
          item.vendorTargetCost,
        ),
      );
      if (original > group.actualTargetCost) group.actualTargetCost = original;

      const vendorAmount = Number(
        item.vendorAmount ||
          Number(item.vendorQty || 0) * Number(item.vendorRate || 0),
      );

      group.vendorEntries.push({
        vendorId: vendor.vendorId,
        vendorName: vendor.vendorName,
        vendorCode: vendor.vendorCode,
        vendorType: vendor.vendorType,
        vendorAmount,
      });
    });
  });

  const itemComparisonRows = Object.values(itemGroups).map((group) => {
    const totalVendorAmount = group.vendorEntries.reduce(
      (sum, v) => sum + v.vendorAmount,
      0,
    );

    return {
      itemName: group.itemName,
      targetCost: group.actualTargetCost,
      hasMissingTarget: group.hasMissingTarget,
      vendorEntries: group.vendorEntries,
      vendorAmount: totalVendorAmount,
      difference: totalVendorAmount - group.actualTargetCost,
      sharedCount: group.vendorEntries.length,
    };
  });

  const sortedItemRows = sortRows(itemComparisonRows, itemSort.sort, {
    itemName: (r) => r.itemName,
    targetCost: (r) => Number(r.targetCost || 0),
    vendorName: (r) => r.vendorEntries.map((v) => v.vendorName).join(", "),
    vendorType: (r) =>
      r.vendorEntries.map((v) => resolveVendorType(v)).join(", "),
    vendorAmount: (r) => Number(r.vendorAmount || 0),
    difference: (r) => Number(r.difference || 0),
  });
  const totalItemTarget = itemComparisonRows.reduce(
    (sum, item) => sum + item.targetCost,
    0,
  );

  const totalItemVendor = itemComparisonRows.reduce(
    (sum, item) => sum + item.vendorAmount,
    0,
  );

  const totalItemDifference = totalItemVendor - totalItemTarget;
  const hasAnyMissingItemTarget = itemComparisonRows.some(
    (row) => row.hasMissingTarget,
  );

  const duplicateAlertRows = [];

  poRows.forEach((row) => {
    const currentPO = Array.isArray(row.items) ? row.items : [];

    const itemNames = currentPO.map(getItemKey).filter(Boolean);

    const sameVendorDuplicates = [];

    itemNames.forEach((name, index) => {
      if (itemNames.indexOf(name) !== index) {
        if (!sameVendorDuplicates.includes(name)) {
          sameVendorDuplicates.push(name);
        }
      }
    });

    sameVendorDuplicates.forEach((itemName) => {
      const duplicateItems = currentPO.filter(
        (item) => getItemKey(item) === itemName,
      );

      const totalQty = duplicateItems.reduce(
        (sum, item) => sum + getItemQty(item),
        0,
      );

      duplicateAlertRows.push({
        vendorId: row.vendorId,
        vendorName: row.vendorName,
        vendorType: row.vendorType,
        vendorCode: row.vendorCode,
        itemName,
        qty: totalQty,
        type: "Same Vendor Duplicate",
        otherVendor: "-",
      });
    });

    poRows.forEach((otherRow) => {
      if (String(otherRow.vendorId) === String(row.vendorId)) return;

      const otherItems = Array.isArray(otherRow.items) ? otherRow.items : [];

      currentPO.forEach((currentItem) => {
        const currentItemName = getItemKey(currentItem);
        if (!currentItemName) return;

        const isInOtherVendor = otherItems.some(
          (otherItem) => getItemKey(otherItem) === currentItemName,
        );

        if (!isInOtherVendor) return;

        const alreadyAdded = duplicateAlertRows.some(
          (x) =>
            x.vendorName === row.vendorName &&
            x.itemName === currentItemName &&
            x.otherVendor === otherRow.vendorName &&
            x.type === "Other Vendor Duplicate",
        );

        if (!alreadyAdded) {
          duplicateAlertRows.push({
            vendorId: row.vendorId,
            vendorName: row.vendorName,
            vendorType: row.vendorType,
            vendorCode: row.vendorCode,
            itemName: currentItemName,
            qty: getItemQty(currentItem),
            type: "Other Vendor Duplicate",
            otherVendor: otherRow.vendorName,
          });
        }
      });
    });
  });

  const sortedDuplicateRows = sortRows(duplicateAlertRows, duplicateSort.sort, {
    vendorName: (r) => r.vendorName,
    vendorType: (r) => resolveVendorType(r),
    vendorCode: (r) => r.vendorCode,
    itemName: (r) => r.itemName,
    qty: (r) => Number(r.qty || 0),
    type: (r) => r.type,
    otherVendor: (r) => r.otherVendor,
  });
  const selectedVendorPaymentInfo = (() => {
    const vendorId = expense.vendorId;

    if (!vendorId) {
      return {
        poTotal: 0,
        paidAmount: 0,
        inProcessAmount: 0,
        balanceAmount: 0,
        afterThisPaymentBalance: 0,
      };
    }

    const vendorPO = poRows.find(
      (row) => String(row.vendorId) === String(vendorId),
    );

    const poTotal = Number(vendorPO?.totalVendorCost || 0);

    const vendorPayments = allPaymentRows.filter(
      (r) => String(r.vendorId) === String(vendorId),
    );

    const paidAmount = vendorPayments
      .filter((r) => r.status === "PAID")
      .reduce((sum, r) => sum + Number(r.paymentAmt || 0), 0);

    const inProcessAmount = vendorPayments
      .filter((r) => ["PENDING_APPROVAL", "APPROVED"].includes(r.status))
      .reduce((sum, r) => sum + Number(r.paymentAmt || 0), 0);

    const balanceAmount = Math.max(poTotal - paidAmount - inProcessAmount, 0);
    const currentPayment = Number(expense.paymentAmt || 0);
    const afterThisPaymentBalance = Math.max(balanceAmount - currentPayment, 0);

    return {
      poTotal,
      paidAmount,
      inProcessAmount,
      balanceAmount,
      afterThisPaymentBalance,
    };
  })();

  // Ledger is read-only and uses existing project PO and payment APIs.
  const ledgerVendorPOs = poRows.filter(
    (row) => String(row.vendorId || "") === ledgerVendorId,
  );
  const ledgerVendor = ledgerVendorPOs[0];
  const ledgerQuoteTotal = ledgerVendorPOs.reduce(
    (sum, row) => sum + Number(row.totalVendorCost || 0),
    0,
  );
  const ledgerPayments = allPaymentRows
    .filter(
      (row) => ledgerVendorId && String(row.vendorId || "") === ledgerVendorId,
    )
    .filter((row) =>
      ["PENDING_APPROVAL", "APPROVED", "PAID"].includes(row.status),
    )
    .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  // Keep running balances tied to chronological ledger order, even when display is sorted.
  const ledgerRunningPaid = new Map();
  let ledgerRunning = 0;
  ledgerPayments.forEach((row) => {
    if (row.status === "PAID") ledgerRunning += Number(row.paymentAmt || 0);
    ledgerRunningPaid.set(row, ledgerRunning);
  });
  const sortedLedgerPayments = sortRows(ledgerPayments, ledgerSort.sort, {
    date: (r) => new Date(r.date || 0).getTime(),
    workType: (r) => r.workType,
    mode: (r) => r.mode,
    paymentAmt: (r) => Number(r.paymentAmt || 0),
    status: (r) => r.status,
    runningPaid: (r) => ledgerRunningPaid.get(r),
    remaining: (r) => ledgerQuoteTotal - ledgerRunningPaid.get(r),
    proof: (r) => (r.paymentProof ? "Yes" : "No"),
  });
  const ledgerPaid = ledgerPayments
    .filter((row) => row.status === "PAID")
    .reduce((sum, row) => sum + Number(row.paymentAmt || 0), 0);
  const ledgerApproved = ledgerPayments
    .filter((row) => row.status === "APPROVED")
    .reduce((sum, row) => sum + Number(row.paymentAmt || 0), 0);
  const ledgerPending = ledgerPayments
    .filter((row) => row.status === "PENDING_APPROVAL")
    .reduce((sum, row) => sum + Number(row.paymentAmt || 0), 0);
  const ledgerBalance = ledgerQuoteTotal - ledgerPaid;

  const exportLedgerExcel = async () => {
    if (!canExportExcel || !ledgerVendor) return;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Vendor Payment Ledger");
    sheet.addRow(["Project", project?.name || ""]);
    sheet.addRow(["Vendor", ledgerVendor.vendorName || ""]);
    sheet.addRow(["Vendor Code", ledgerVendor.vendorCode || ""]);
    sheet.addRow(["Quote / PO Amount", ledgerQuoteTotal]);
    sheet.addRow(["Total Paid", ledgerPaid]);
    sheet.addRow(["Approved, Not Paid", ledgerApproved]);
    sheet.addRow(["Pending Approval", ledgerPending]);
    sheet.addRow(["Balance After Paid", ledgerBalance]);
    sheet.addRow([]);
    const header = sheet.addRow([
      "Sr No",
      "Date",
      "Work Type",
      "Payment Mode",
      "Payment Amount",
      "Status",
      "Paid Running Total",
      "Remaining After Paid",
      "Payment Proof URL",
    ]);
    header.font = { bold: true };
    let runningPaid = 0;
    ledgerPayments.forEach((row, index) => {
      if (row.status === "PAID") runningPaid += Number(row.paymentAmt || 0);
      sheet.addRow([
        index + 1,
        formatDate(row.date),
        row.workType || "",
        row.mode || "",
        Number(row.paymentAmt || 0),
        row.status || "",
        runningPaid,
        ledgerQuoteTotal - runningPaid,
        row.paymentProof ? getPaymentProofUrl(row.paymentProof) : "",
      ]);
    });
    sheet.columns.forEach((column) => {
      column.width = 23;
    });
    [4, 5, 6, 7, 8].forEach((n) => {
      sheet.getRow(n).getCell(2).numFmt = "#,##0.00";
    });
    for (let i = 11; i <= sheet.rowCount; i += 1) {
      [5, 7, 8].forEach((n) => {
        sheet.getRow(i).getCell(n).numFmt = "#,##0.00";
      });
    }
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `VendorLedger_${String(ledgerVendor.vendorCode || ledgerVendor.vendorName || "Vendor").replace(/[^a-z0-9_-]/gi, "_")}.xlsx`,
    );
  };

  const exportToExcel = async () => {
    if (!canExportExcel) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Vendor PO Summary");

    worksheet.addRow([
      "Sr No",
      "Vendor Name",
      "Vendor Type",
      "Vendor Code",
      "Total Target Cost",
      "Net Payable (GST added, TDS deducted if applicable)",
      "Difference",
    ]);

    adjustedPoRows.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.vendorName || "",
        resolveVendorType(row),
        row.vendorCode || "",
        row.totalTargetCost || 0,
        row.totalVendorCost || 0,
        row.difference || 0,
      ]);
    });

    worksheet.addRow([]);
    worksheet.addRow([
      "",
      "",
      "",
      "Total",
      totalTargetCost,
      totalVendorAmount,
      totalDifference,
    ]);

    if (duplicateAlertRows.length > 0) {
      worksheet.addRow([]);
      worksheet.addRow(["Duplicate Item Alert Summary"]);
      worksheet.addRow([
        "Sr No",
        "Vendor Name",
        "Vendor Type",
        "Vendor Code",
        "Item Name",
        "Qty",
        "Alert Type",
        "Other Vendor",
      ]);

      duplicateAlertRows.forEach((r, index) => {
        worksheet.addRow([
          index + 1,
          r.vendorName,
          resolveVendorType(r),
          r.vendorCode,
          r.itemName,
          r.qty,
          r.type,
          r.otherVendor,
        ]);
      });
    }

    worksheet.columns.forEach((col) => {
      col.width = 25;
      col.alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
    });

    worksheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, "VendorPOSummary.xlsx");
  };

  const addExpense = async () => {
    if (!canCreatePayment) return;

    try {
      if (
        !expense.date ||
        !expense.vendorId ||
        !expense.paymentAmt ||
        !expense.mode
      ) {
        alert("Please select Vendor, and fill Date, Amount, Mode.");
        return;
      }

      const paymentAmount = Number(expense.paymentAmt || 0);

      if (selectedVendorPaymentInfo.poTotal <= 0) {
        alert(
          "This vendor has no Vendor PO total. Please create Vendor PO first.",
        );
        return;
      }

      if (paymentAmount <= 0) {
        alert("Payment amount must be greater than 0.");
        return;
      }

      if (paymentAmount > selectedVendorPaymentInfo.balanceAmount) {
        alert(
          `Payment cannot be more than balance amount.\n\nPO Total: ${formatINR(
            selectedVendorPaymentInfo.poTotal,
          )}\nPaid: ${formatINR(
            selectedVendorPaymentInfo.paidAmount,
          )}\nIn Process: ${formatINR(
            selectedVendorPaymentInfo.inProcessAmount,
          )}\nBalance: ${formatINR(selectedVendorPaymentInfo.balanceAmount)}`,
        );
        return;
      }

      const payload = {
        projectName: project?.name || "",
        projectId: project?._id,
        date: expense.date,
        vendorId: expense.vendorId,
        vendorName: expense.vendorName || "",
        vendorCode: expense.vendorCode || "",
        workType: expense.workType || "",
        paymentAmt: paymentAmount,
        mode: expense.mode,
        gstInvoice: expense.gstInvoice || "NO",
        poTotal: selectedVendorPaymentInfo.poTotal,
        paidAmount: selectedVendorPaymentInfo.paidAmount,
        inProcessAmount:
          selectedVendorPaymentInfo.inProcessAmount + paymentAmount,
        balanceAmount: selectedVendorPaymentInfo.balanceAmount - paymentAmount,
        employeeNote: expense.employeeNote || "",
      };

      await axios.post(
        `${BASE_URL}/api/vendor-payment-requests`,
        payload,
        getAuthConfig(),
      );

      alert("Payment intimation sent for admin approval.");
      setExpense(emptyExpense);
      fetchPaymentRequests();
    } catch (error) {
      console.error("Add expense error:", error?.response?.data || error);
      alert(
        error?.response?.data?.message ||
          "Unauthorized or failed to create payment intimation.",
      );
    }
  };

  const approveRequest = async (id) => {
    if (!canApproveReject) return;

    try {
      const request = allPaymentRows.find((row) => row._id === id);
      const remark = window.prompt("Enter admin approval remark:", "") || "";

      await axios.put(
        `${BASE_URL}/api/vendor-payment-requests/${id}/approve`,
        {
          approvalRemark: remark,
          paymentAmt: Number(request?.paymentAmt || 0),
          mode: request?.mode || "",
        },
        getAuthConfig(),
      );

      fetchPaymentRequests();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to approve request.");
    }
  };

  const rejectRequest = async (id) => {
    if (!canApproveReject) return;

    try {
      const remark = window.prompt("Enter rejection remark:", "") || "";

      await axios.put(
        `${BASE_URL}/api/vendor-payment-requests/${id}/reject`,
        { rejectRemark: remark },
        getAuthConfig(),
      );

      fetchPaymentRequests();
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to reject request.");
    }
  };
  // User can save only their own note on pending/approved requests.
  // The backend independently enforces ownership, role and request status.
  const saveUserNote = async (id, employeeNote) => {
    const request = allPaymentRows.find(
      (row) => String(row._id) === String(id),
    );
    const ownerId = String(request?.createdBy?._id || request?.createdBy || "");
    if (
      noteSavingId ||
      !request ||
      !["PENDING_APPROVAL", "APPROVED"].includes(request.status) ||
      (!isAdmin && (!currentUserId || ownerId !== currentUserId))
    )
      return false;
    if (typeof employeeNote !== "string" || employeeNote.length > 3000) {
      alert("Note must be 3000 characters or fewer.");
      return false;
    }

    setNoteSavingId(id);
    try {
      await axios.patch(
        `${BASE_URL}/api/vendor-payment-requests/${id}/note`,
        { employeeNote },
        getAuthConfig(),
      );
      await fetchPaymentRequests();
      return true;
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Note not saved. Please install the /:id/note PATCH backend route.",
      );
      return false;
    } finally {
      setNoteSavingId(null);
    }
  };

  // Admin-only UI. Backend MUST independently verify JWT role and totals.
  const startEditApproved = (request) => {
    if (!isAdmin || request?.status !== "APPROVED") return;
    setEditingApproved(request);
    setApprovedEditForm({
      date: request.date ? String(request.date).slice(0, 10) : "",
      paymentAmt: String(request.paymentAmt ?? ""),
      mode: request.mode || "",
      gstInvoice: request.gstInvoice || "NO",
      workType: request.workType || "",
      note: request.note || "",
      approvalRemark: request.approvalRemark || "",
    });
  };

  const saveApprovedEdit = async (event) => {
    event.preventDefault();
    if (
      !isAdmin ||
      editingApproved?.status !== "APPROVED" ||
      !approvedEditForm ||
      approvedActionBusy
    )
      return;
    const paymentAmt = Number(approvedEditForm.paymentAmt);
    if (
      !approvedEditForm.date ||
      !Number.isFinite(paymentAmt) ||
      paymentAmt <= 0 ||
      !approvedEditForm.mode
    ) {
      alert("Enter a valid date, positive amount, and payment mode.");
      return;
    }
    setApprovedActionBusy(true);
    try {
      await axios.put(
        `${BASE_URL}/api/vendor-payment-requests/${editingApproved._id}`,
        {
          date: approvedEditForm.date,
          paymentAmt,
          mode: approvedEditForm.mode,
          gstInvoice: approvedEditForm.gstInvoice,
          workType: approvedEditForm.workType,
          note: approvedEditForm.note,
          approvalRemark: approvedEditForm.approvalRemark,
        },
        getAuthConfig(),
      );
      setEditingApproved(null);
      setApprovedEditForm(null);
      await Promise.all([fetchPaymentRequests(), fetchVendorPOSummary()]);
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Could not edit approved request. Check admin-only PUT route.",
      );
    } finally {
      setApprovedActionBusy(false);
    }
  };

  const deleteApprovedRequest = async (request) => {
    if (!isAdmin || request?.status !== "APPROVED" || approvedActionBusy)
      return;
    if (
      !window.confirm(
        `Delete approved payment request for ${request.vendorName || "vendor"} (₹${formatINR(request.paymentAmt)})? This cannot be undone.`,
      )
    )
      return;
    setApprovedActionBusy(true);
    try {
      await axios.delete(
        `${BASE_URL}/api/vendor-payment-requests/${request._id}`,
        getAuthConfig(),
      );
      await Promise.all([fetchPaymentRequests(), fetchVendorPOSummary()]);
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Could not delete request. Check admin-only DELETE route.",
      );
    } finally {
      setApprovedActionBusy(false);
    }
  };

  const markPaidRequest = (id, selectedDate = "") => {
    if (!canMarkPaid) return;

    const request = allPaymentRows.find(
      (row) => String(row._id) === String(id),
    );

    if (!request || request.status !== "APPROVED") {
      alert("Only approved requests can be marked paid.");
      return;
    }

    setMarkPaidModal(request);
    setActualPaymentDate(selectedDate);
    setActualPaymentProof(null);
  };

  const submitMarkPaid = async (event) => {
    event.preventDefault();

    if (!canMarkPaid || !markPaidModal || markPaidSaving) {
      return;
    }

    if (!actualPaymentDate) {
      alert("Please select actual payment date.");
      return;
    }

    if (!actualPaymentProof) {
      alert("Please upload payment proof.");
      return;
    }

    setMarkPaidSaving(true);

    try {
      const formData = new FormData();

      formData.append("paymentDate", actualPaymentDate);
      formData.append("paymentProof", actualPaymentProof);

      await axios.put(
        `${BASE_URL}/api/vendor-payment-requests/${markPaidModal._id}/mark-paid`,
        formData,
        {
          headers: {
            ...getAuthConfig().headers,
          },
        },
      );

      alert("Payment marked as paid successfully.");

      setMarkPaidModal(null);
      setActualPaymentDate("");
      setActualPaymentProof(null);

      await Promise.all([fetchPaymentRequests(), fetchVendorPOSummary()]);
    } catch (error) {
      console.error("Mark paid error:", error?.response?.data || error);

      alert(
        error?.response?.data?.message || "Failed to mark payment as paid.",
      );
    } finally {
      setMarkPaidSaving(false);
    }
  };

  if (!canViewVendorQuote) {
    return (
      <div className="vq-no-access">
        You do not have permission to view vendor quote details.
      </div>
    );
  }

  return (
    <div className="vq-page">
      <header className="vq-page-header">
        <button
          className="vq-btn vq-btn-back"
          onClick={() => navigate("/project")}
        >
          ← Back
        </button>

        <div className="vq-header-center">
          <div className="vq-eyebrow">PROCUREMENT</div>
          <h2>Vendor PO Summary</h2>
          <p>{project?.name || "Project"}</p>
        </div>

        {canSeeVendorButton ? (
          <div className="vq-header-actions">
            {canViewVendorPO && (
              <button
                className="vq-btn vq-btn-secondary"
                onClick={() => navigate("/vendor-po", { state: { project } })}
              >
                All Vendors PO
              </button>
            )}

            {canShowVendorMaster && (
              <button
                className="vq-btn vq-btn-success"
                onClick={() => setShowVendorForm(true)}
              >
                Vendor Master
              </button>
            )}
          </div>
        ) : (
          <div />
        )}
      </header>

      {canShowPOSummary && (
        <section className="vq-card">
          <div className="vq-card-head">
            <div>
              <div className="vq-eyebrow">PO OVERVIEW</div>
              <h3>Vendor PO Summary</h3>
            </div>
            <div className="vq-pill">{adjustedPoRows.length} Vendors</div>
          </div>

          <div className="vq-table-scroll">
            <table className="vq-table vq-summary-table">
              <thead>
                <tr>
                  <th className="vq-center">Sr No</th>
                  <SortTh
                    label="Vendor Name"
                    sortKey="vendorName"
                    sort={poSort.sort}
                    toggle={poSort.toggle}
                  />
                  <SortTh
                    label="Vendor Type"
                    sortKey="vendorType"
                    sort={poSort.sort}
                    toggle={poSort.toggle}
                  />
                  <SortTh
                    label="Vendor Code"
                    sortKey="vendorCode"
                    sort={poSort.sort}
                    toggle={poSort.toggle}
                  />
                  <SortTh
                    className="vq-num"
                    label="Total Target Cost"
                    sortKey="totalTargetCost"
                    sort={poSort.sort}
                    toggle={poSort.toggle}
                  />
                  <SortTh
                    className="vq-num"
                    label="Net Payable"
                    sortKey="totalVendorCost"
                    sort={poSort.sort}
                    toggle={poSort.toggle}
                  />
                  <SortTh
                    className="vq-num"
                    label="Total Difference"
                    sortKey="difference"
                    sort={poSort.sort}
                    toggle={poSort.toggle}
                  />
                </tr>
              </thead>

              <tbody>
                {loadingPO ? (
                  <tr>
                    <td colSpan="7" className="vq-empty-state">
                      Loading Vendor PO Summary...
                    </td>
                  </tr>
                ) : adjustedPoRows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="vq-empty-state">
                      No Vendor PO entry found. Please create Vendor PO first.
                    </td>
                  </tr>
                ) : (
                  sortedPoRows.map((row, index) => (
                    <tr
                      key={row.poId || index}
                      className={canViewVendorPO ? "vq-clickable-row" : ""}
                      onClick={() => {
                        if (!canViewVendorPO) return;

                        navigate(`/vendor-po/${row.vendorId}`, {
                          state: {
                            project,
                            vendor: {
                              _id: row.vendorId,
                              fullName: row.vendorName,
                              vendorType: resolveVendorType(row),
                              vendorCode: row.vendorCode,
                            },
                          },
                        });
                      }}
                    >
                      <td className="vq-center">{index + 1}</td>
                      <td className="vq-strong vq-primary-text">
                        {row.vendorName || "-"}
                      </td>
                      <td>{resolveVendorType(row)}</td>
                      <td>{row.vendorCode || "-"}</td>
                      <td className="vq-num">
                        {row.hasMissingTarget
                          ? "Target not set"
                          : formatINR(row.totalTargetCost)}
                        {row.hasSharedItem && (
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 10,
                              fontWeight: 900,
                              color: "#b45309",
                            }}
                          >
                            Includes item(s) split with other vendor(s)
                          </div>
                        )}
                      </td>
                      <td className="vq-num vq-strong">
                        {formatINR(row.totalVendorCost)}
                        {(row.gstEnabled || row.tdsEnabled) && (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 10,
                              color: "#64748b",
                              fontWeight: 700,
                              lineHeight: 1.5,
                            }}
                          >
                            Base: {formatINR(row.baseAmount)}
                            {row.gstEnabled && (
                              <div>
                                GST (+{row.gstRate}%):{" "}
                                {formatINR(row.gstAmount)}
                              </div>
                            )}
                            {row.tdsEnabled && (
                              <div>
                                TDS (−{row.tdsRate}%):{" "}
                                {formatINR(row.tdsAmount)}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td
                        className={`vq-num vq-strong ${
                          row.difference >= 0 ? "vq-positive" : "vq-negative"
                        }`}
                      >
                        {row.hasMissingTarget
                          ? "—"
                          : formatDifferenceWithPercent(
                              row.difference,
                              row.totalTargetCost,
                            )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {adjustedPoRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="4">TOTAL</td>
                    <td className="vq-num">
                      {hasAnyMissingTarget
                        ? "Incomplete"
                        : formatINR(totalTargetCost)}
                    </td>
                    <td className="vq-num">{formatINR(totalVendorAmount)}</td>
                    <td
                      className={`vq-num ${
                        totalDifference >= 0 ? "vq-positive" : "vq-negative"
                      }`}
                    >
                      {hasAnyMissingTarget
                        ? "—"
                        : formatDifferenceWithPercent(
                            totalDifference,
                            totalTargetCost,
                          )}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      <section className="vq-card">
        <div className="vq-card-head">
          <div>
            <div className="vq-eyebrow">PRICE COMPARISON</div>
            <h3>Quote Item vs Vendor Price</h3>
          </div>

          <div className="vq-kpi-inline">
            <span>Target {formatINR(totalItemTarget)}</span>
            <span>Vendor {formatINR(totalItemVendor)}</span>
          </div>
        </div>

        <div className="vq-table-scroll">
          <table className="vq-table vq-comparison-table">
            <thead>
              <tr>
                <th className="vq-center vq-col-sr">Sr No</th>
                <SortTh
                  className="vq-col-item"
                  label="Quote Item"
                  sortKey="itemName"
                  sort={itemSort.sort}
                  toggle={itemSort.toggle}
                />
                <SortTh
                  className="vq-num vq-col-money"
                  label="Target Cost"
                  sortKey="targetCost"
                  sort={itemSort.sort}
                  toggle={itemSort.toggle}
                />
                <SortTh
                  className="vq-col-vendor"
                  label="Vendor Name"
                  sortKey="vendorName"
                  sort={itemSort.sort}
                  toggle={itemSort.toggle}
                />
                <SortTh
                  className="vq-col-vendor"
                  label="Vendor Type"
                  sortKey="vendorType"
                  sort={itemSort.sort}
                  toggle={itemSort.toggle}
                />
                <SortTh
                  className="vq-num vq-col-money"
                  label="Vendor Amount"
                  sortKey="vendorAmount"
                  sort={itemSort.sort}
                  toggle={itemSort.toggle}
                />
                <SortTh
                  className="vq-num vq-col-diff"
                  label="Difference"
                  sortKey="difference"
                  sort={itemSort.sort}
                  toggle={itemSort.toggle}
                />
              </tr>
            </thead>

            <tbody>
              {itemComparisonRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="vq-empty-state">
                    No vendor PO items available.
                  </td>
                </tr>
              ) : (
                sortedItemRows.map((row, index) => (
                  <tr key={row.itemName || index}>
                    <td className="vq-center">{index + 1}</td>
                    <td className="vq-item-cell">
                      {row.itemName}
                      {row.sharedCount > 1 && (
                        <div
                          style={{
                            marginTop: 3,
                            fontSize: 10,
                            fontWeight: 900,
                            color: "#b45309",
                          }}
                        >
                          Quoted by {row.sharedCount} vendors
                        </div>
                      )}
                    </td>
                    <td className="vq-num">
                      {row.hasMissingTarget
                        ? "Target not set"
                        : formatINR(row.targetCost)}
                    </td>
                    <td className="vq-strong">
                      {row.vendorEntries.length > 1 ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {row.vendorEntries.map((v, i) => (
                            <span key={i}>
                              {v.vendorName}{" "}
                              <span
                                style={{
                                  color: "#64748b",
                                  fontWeight: 700,
                                  fontSize: 12,
                                }}
                              >
                                ({formatINR(v.vendorAmount)})
                              </span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        row.vendorEntries[0]?.vendorName || "-"
                      )}
                    </td>
                    <td>
                      {row.vendorEntries.length > 1 ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {row.vendorEntries.map((v, i) => (
                            <span key={i}>{resolveVendorType(v)}</span>
                          ))}
                        </div>
                      ) : (
                        resolveVendorType(row.vendorEntries[0])
                      )}
                    </td>
                    <td className="vq-num vq-strong">
                      {formatINR(row.vendorAmount)}
                    </td>
                    <td
                      className={`vq-num vq-strong ${
                        row.difference > 0 ? "vq-negative" : "vq-positive"
                      }`}
                    >
                      {row.hasMissingTarget
                        ? "—"
                        : formatDifferenceWithPercent(
                            -row.difference,
                            row.targetCost,
                          )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {itemComparisonRows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={2}>TOTAL</td>
                  <td className="vq-num">
                    {hasAnyMissingItemTarget
                      ? "Incomplete"
                      : formatINR(totalItemTarget)}
                  </td>
                  <td></td>
                  <td></td>
                  <td className="vq-num">{formatINR(totalItemVendor)}</td>
                  <td
                    className={`vq-num ${
                      totalItemDifference > 0 ? "vq-negative" : "vq-positive"
                    }`}
                  >
                    {hasAnyMissingItemTarget
                      ? "—"
                      : formatDifferenceWithPercent(
                          -totalItemDifference,
                          totalItemTarget,
                        )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {canShowDuplicateAlert && duplicateAlertRows.length > 0 && (
        <section className="vq-card vq-alert-card">
          <div className="vq-card-head">
            <div>
              <div className="vq-eyebrow vq-eyebrow-danger">ATTENTION</div>
              <h3>Duplicate Item Alert Summary</h3>
            </div>
            <div className="vq-pill vq-pill-danger">
              {duplicateAlertRows.length} Alerts
            </div>
          </div>

          <div className="vq-table-scroll">
            <table className="vq-table vq-duplicate-table">
              <thead>
                <tr>
                  <th className="vq-center">Sr No</th>
                  <SortTh
                    label="Vendor Name"
                    sortKey="vendorName"
                    sort={duplicateSort.sort}
                    toggle={duplicateSort.toggle}
                  />
                  <SortTh
                    label="Vendor Type"
                    sortKey="vendorType"
                    sort={duplicateSort.sort}
                    toggle={duplicateSort.toggle}
                  />
                  <SortTh
                    label="Vendor Code"
                    sortKey="vendorCode"
                    sort={duplicateSort.sort}
                    toggle={duplicateSort.toggle}
                  />
                  <SortTh
                    label="Item Name"
                    sortKey="itemName"
                    sort={duplicateSort.sort}
                    toggle={duplicateSort.toggle}
                  />
                  <SortTh
                    className="vq-center"
                    label="Qty"
                    sortKey="qty"
                    sort={duplicateSort.sort}
                    toggle={duplicateSort.toggle}
                  />
                  <SortTh
                    label="Alert Type"
                    sortKey="type"
                    sort={duplicateSort.sort}
                    toggle={duplicateSort.toggle}
                  />
                  <SortTh
                    label="Other Vendor"
                    sortKey="otherVendor"
                    sort={duplicateSort.sort}
                    toggle={duplicateSort.toggle}
                  />
                </tr>
              </thead>

              <tbody>
                {sortedDuplicateRows.map((r, index) => (
                  <tr key={index}>
                    <td className="vq-center">{index + 1}</td>
                    <td>{r.vendorName}</td>
                    <td>{resolveVendorType(r)}</td>
                    <td>{r.vendorCode}</td>
                    <td className="vq-negative vq-strong">{r.itemName}</td>
                    <td className="vq-center vq-strong">{r.qty || 0}</td>
                    <td>{r.type}</td>
                    <td>{r.otherVendor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {canExportExcel && (
        <div className="vq-export-row">
          <button
            className="vq-btn vq-btn-outline"
            onClick={exportToExcel}
            disabled={!poRows.length}
          >
            Export to Excel
          </button>
        </div>
      )}

      {canCreatePayment && (
        <Block title="CREATE PAYMENT INTIMATION" variant="payment">
          <div className="vq-payment-grid">
            <Input
              placeholder="Date"
              type="date"
              value={expense.date}
              onChange={(v) => setExpense({ ...expense, date: v })}
            />

            <select
              value={expense.vendorId || ""}
              onChange={(e) => {
                const id = e.target.value;
                const v = vendors.find((x) => x._id === id);

                setExpense((prev) => ({
                  ...prev,
                  vendorId: id,
                  vendorName: v?.fullName || "",
                  vendorCode: v?.vendorCode || "",
                  workType: v?.vendorType || prev.workType,
                  paymentAmt: "",
                  employeeNote: "",
                }));
              }}
              className="vq-input vq-select"
            >
              <option value="">Select Vendor Name</option>
              {vendors.map((v) => (
                <option key={v._id} value={v._id}>
                  {(v.vendorCode || "NA") + " — " + (v.vendorType || "Type")} —{" "}
                  {v.fullName}
                </option>
              ))}
            </select>

            <Input
              placeholder="Type of Work"
              value={expense.workType}
              onChange={(v) => setExpense({ ...expense, workType: v })}
            />

            <Input
              placeholder="Payment Amt"
              type="number"
              value={expense.paymentAmt}
              onChange={(v) => setExpense({ ...expense, paymentAmt: v })}
            />

            <Select
              value={expense.mode}
              onChange={(v) => setExpense({ ...expense, mode: v })}
              options={paymentModes}
              placeholder="Mode"
            />

            <Select
              value={expense.gstInvoice}
              onChange={(v) => setExpense({ ...expense, gstInvoice: v })}
              options={["YES", "NO"]}
              placeholder="GST Invoice?"
            />

            {/* <label
              style={{
                gridColumn: "1 / -1",
                display: "grid",
                gap: 5,
                minWidth: 0,
                fontWeight: 600,
              }}
            >
              User Note / Remark
              <textarea
                className="vq-input"
                placeholder="Write a note for this payment (multiple lines allowed)"
                rows={3}
                maxLength={3000}
                value={expense.employeeNote}
                onChange={(e) =>
                  setExpense((prev) => ({
                    ...prev,
                    employeeNote: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  minHeight: 74,
                  boxSizing: "border-box",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  resize: "vertical",
                  fontWeight: 400,
                }}
              />
            </label> */}

            <button className="vq-btn vq-btn-danger" onClick={addExpense}>
              Send
            </button>

            {canShowPaymentInfo && expense.vendorId && (
              <div className="vq-payment-info">
                <span>
                  PO Total: {formatINR(selectedVendorPaymentInfo.poTotal)}
                </span>
                <span>
                  Paid: {formatINR(selectedVendorPaymentInfo.paidAmount)}
                </span>
                <span>
                  Approval Pending:{" "}
                  {formatINR(selectedVendorPaymentInfo.inProcessAmount)}
                </span>
                <span>
                  Pending Payment:{" "}
                  {formatINR(selectedVendorPaymentInfo.balanceAmount)}
                </span>
                {Number(expense.paymentAmt || 0) > 0 && (
                  <span>
                    Pending After This:{" "}
                    {formatINR(
                      selectedVendorPaymentInfo.afterThisPaymentBalance,
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </Block>
      )}

      {(canShowPendingApproval || canShowApprovedRequests) && (
        <>
          {canShowPendingApproval && (
            <Block title="PENDING APPROVAL REQUESTS" variant="pending">
              <RequestTable
                rows={pendingApprovalRows}
                resolveVendorType={resolveVendorType}
                showApprove={canApproveReject}
                showReject={canApproveReject}
                onApprove={approveRequest}
                onReject={rejectRequest}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                onSaveNote={saveUserNote}
                noteSavingId={noteSavingId}
              />
            </Block>
          )}

          {canShowApprovedRequests && (
            <Block title="APPROVED REQUESTS" variant="approved">
              <RequestTable
                rows={approvedRows}
                resolveVendorType={resolveVendorType}
                showMarkPaid={canMarkPaid}
                onMarkPaid={markPaidRequest}
                showAdminActions={isAdmin}
                onEdit={startEditApproved}
                onDelete={deleteApprovedRequest}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                onSaveNote={saveUserNote}
                noteSavingId={noteSavingId}
              />
            </Block>
          )}
        </>
      )}

      {canShowApprovedRequests && (
        <Block title="VENDOR PAYMENT LEDGER" variant="approved">
          <div className="vq-ledger-toolbar">
            <label htmlFor="vq-ledger-vendor">Select Project Vendor</label>
            <select
              id="vq-ledger-vendor"
              className="vq-input vq-select"
              value={ledgerVendorId}
              onChange={(event) => setLedgerVendorId(event.target.value)}
            >
              <option value="">Select vendor</option>
              {Array.from(
                new Map(
                  poRows
                    .filter((row) => row.vendorId)
                    .map((row) => [String(row.vendorId), row]),
                ).values(),
              ).map((row) => (
                <option key={String(row.vendorId)} value={String(row.vendorId)}>
                  {row.vendorName} ({row.vendorCode || "No code"})
                </option>
              ))}
            </select>
            {canExportExcel && (
              <button
                type="button"
                className="vq-btn vq-btn-success"
                disabled={!ledgerVendor}
                onClick={exportLedgerExcel}
              >
                Export Ledger Excel
              </button>
            )}
          </div>
          {!ledgerVendor ? (
            <div className="vq-empty-state">
              Select a vendor to view the project ledger.
            </div>
          ) : (
            <>
              <div className="vq-ledger-cards">
                <div>
                  <span>Quote / PO Amount</span>
                  <strong>{formatINR(ledgerQuoteTotal)}</strong>
                </div>
                <div>
                  <span>Total Paid</span>
                  <strong>{formatINR(ledgerPaid)}</strong>
                </div>
                <div>
                  <span>Approved, Not Paid</span>
                  <strong>{formatINR(ledgerApproved)}</strong>
                </div>
                <div>
                  <span>Pending Approval</span>
                  <strong>{formatINR(ledgerPending)}</strong>
                </div>
                <div>
                  <span>Balance After Paid</span>
                  <strong>{formatINR(ledgerBalance)}</strong>
                </div>
              </div>
              <div className="vq-table-scroll">
                <table className="vq-table vq-ledger-table">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <SortTh
                        label="Date"
                        sortKey="date"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                      <SortTh
                        label="Work Type"
                        sortKey="workType"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                      <SortTh
                        label="Mode"
                        sortKey="mode"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                      <SortTh
                        className="vq-num"
                        label="Payment Amount"
                        sortKey="paymentAmt"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                      <SortTh
                        label="Status"
                        sortKey="status"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                      <SortTh
                        className="vq-num"
                        label="Running Paid"
                        sortKey="runningPaid"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                      <SortTh
                        className="vq-num"
                        label="Remaining After Paid"
                        sortKey="remaining"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                      <SortTh
                        label="Proof"
                        sortKey="proof"
                        sort={ledgerSort.sort}
                        toggle={ledgerSort.toggle}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerPayments.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="vq-empty-state">
                          No payment entries found.
                        </td>
                      </tr>
                    ) : (
                      sortedLedgerPayments.map((row, index) => {
                        const paidToDate = ledgerRunningPaid.get(row) || 0;
                        return (
                          <tr key={row._id || index}>
                            <td>{index + 1}</td>
                            <td>{formatDate(row.date)}</td>
                            <td>{row.workType || "-"}</td>
                            <td>{row.mode || "-"}</td>
                            <td className="vq-num">
                              {formatINR(row.paymentAmt)}
                            </td>
                            <td>
                              <span
                                className={`vq-status vq-status-${String(row.status || "").toLowerCase()}`}
                              >
                                {row.status || "-"}
                              </span>
                            </td>
                            <td className="vq-num">{formatINR(paidToDate)}</td>
                            <td className="vq-num">
                              {formatINR(ledgerQuoteTotal - paidToDate)}
                            </td>
                            <td>
                              {row.paymentProof ? (
                                <a
                                  className="vq-btn vq-btn-outline vq-btn-small"
                                  href={getPaymentProofUrl(row.paymentProof)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Proof
                                </a>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Block>
      )}

      {isAdmin && editingApproved && approvedEditForm && (
        <div className="vq-modal-backdrop" role="presentation">
          <div
            className="vq-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Edit approved payment request"
            style={{ maxWidth: 780, width: "min(780px, 96vw)" }}
          >
            <div className="vq-modal-head">
              <h3 style={{ margin: 0 }}>Edit Approved Payment</h3>
              <button
                type="button"
                className="vq-btn vq-btn-outline vq-btn-small"
                disabled={approvedActionBusy}
                onClick={() => {
                  setEditingApproved(null);
                  setApprovedEditForm(null);
                }}
              >
                Close
              </button>
            </div>
            <p style={{ margin: "8px 0", fontSize: 12 }}>
              Vendor: {editingApproved.vendorName} · Only approved, unpaid
              requests can be edited.
            </p>
            <form
              onSubmit={saveApprovedEdit}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 8,
              }}
            >
              <label>
                Date
                <input
                  className="vq-input"
                  type="date"
                  required
                  value={approvedEditForm.date}
                  onChange={(e) =>
                    setApprovedEditForm((p) => ({ ...p, date: e.target.value }))
                  }
                />
              </label>
              <label>
                Amount
                <input
                  className="vq-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={approvedEditForm.paymentAmt}
                  onChange={(e) =>
                    setApprovedEditForm((p) => ({
                      ...p,
                      paymentAmt: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Mode
                <select
                  className="vq-input vq-select"
                  required
                  value={approvedEditForm.mode}
                  onChange={(e) =>
                    setApprovedEditForm((p) => ({ ...p, mode: e.target.value }))
                  }
                >
                  <option value="">Select</option>
                  {paymentModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                GST Invoice
                <select
                  className="vq-input vq-select"
                  value={approvedEditForm.gstInvoice}
                  onChange={(e) =>
                    setApprovedEditForm((p) => ({
                      ...p,
                      gstInvoice: e.target.value,
                    }))
                  }
                >
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </select>
              </label>
              <label>
                Work Type
                <input
                  className="vq-input"
                  value={approvedEditForm.workType}
                  onChange={(e) =>
                    setApprovedEditForm((p) => ({
                      ...p,
                      workType: e.target.value,
                    }))
                  }
                />
              </label>
              <label style={{ gridColumn: "1 / -1" }}>
                Admin Note
                <textarea
                  className="vq-input"
                  rows={3}
                  maxLength={3000}
                  value={approvedEditForm.note}
                  onChange={(e) =>
                    setApprovedEditForm((p) => ({ ...p, note: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    resize: "vertical",
                  }}
                />
              </label>
              <label style={{ gridColumn: "1 / -1" }}>
                Admin Remark
                <input
                  className="vq-input"
                  value={approvedEditForm.approvalRemark}
                  onChange={(e) =>
                    setApprovedEditForm((p) => ({
                      ...p,
                      approvalRemark: e.target.value,
                    }))
                  }
                />
              </label>
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  className="vq-btn vq-btn-outline"
                  disabled={approvedActionBusy}
                  onClick={() => {
                    setEditingApproved(null);
                    setApprovedEditForm(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="vq-btn vq-btn-primary"
                  disabled={approvedActionBusy}
                >
                  {approvedActionBusy ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {markPaidModal && (
        <div className="vq-modal-backdrop">
          <div className="vq-modal" style={{ maxWidth: 480, width: "95%" }}>
            <div className="vq-modal-head">
              <div>
                <div className="vq-eyebrow">VENDOR PAYMENT</div>
                <h3>Mark Payment as Paid</h3>
              </div>

              <button
                type="button"
                className="vq-btn vq-btn-outline"
                disabled={markPaidSaving}
                onClick={() => {
                  setMarkPaidModal(null);
                  setActualPaymentDate("");
                  setActualPaymentProof(null);
                }}
              >
                Close
              </button>
            </div>

            <form
              onSubmit={submitMarkPaid}
              style={{
                display: "grid",
                gap: 16,
                padding: 16,
              }}
            >
              <div>
                <strong>{markPaidModal.vendorName || "-"}</strong>

                <div style={{ marginTop: 6 }}>
                  Payment Amount: ₹{formatINR(markPaidModal.paymentAmt)}
                </div>

                <div style={{ marginTop: 6 }}>
                  Request Date: {formatDate(markPaidModal.date)}
                </div>
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                  fontWeight: 600,
                }}
              >
                Actual Payment Date *
                <input
                  type="date"
                  className="vq-input"
                  value={actualPaymentDate}
                  onChange={(e) => setActualPaymentDate(e.target.value)}
                  required
                />
              </label>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                  fontWeight: 600,
                }}
              >
                Payment Proof *
                <input
                  type="file"
                  className="vq-input"
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                  onChange={(e) =>
                    setActualPaymentProof(e.target.files?.[0] || null)
                  }
                  required
                />
              </label>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  className="vq-btn vq-btn-outline"
                  disabled={markPaidSaving}
                  onClick={() => {
                    setMarkPaidModal(null);
                    setActualPaymentDate("");
                    setActualPaymentProof(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="vq-btn vq-btn-primary"
                  disabled={markPaidSaving}
                >
                  {markPaidSaving ? "Saving..." : "Confirm Mark Paid"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVendorForm && (
        <div className="vq-modal-backdrop">
          <div className="vq-modal">
            <div className="vq-modal-head">
              <div>
                <div className="vq-eyebrow">VENDOR MASTER</div>
                <h3>Manage Vendors</h3>
              </div>
              <button
                className="vq-btn vq-btn-danger"
                onClick={() => setShowVendorForm(false)}
              >
                Close
              </button>
            </div>

            <VendorMasterForm user={user} />
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorQuote;
